import { toDate } from './mainEventerCooldown'

const toLineupAdviceNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const getMemberName = item => item?.member?.name || ''

const getUpcomingPleTime = item =>
  toDate(item?.upcomingPleMatch?.date)?.getTime?.() || Number.MAX_SAFE_INTEGER

const getUpcomingPleMatch = insight => {
  const upcomingPleMatches = Array.isArray(insight?.upcomingPleMatches)
    ? insight.upcomingPleMatches
    : []

  if (upcomingPleMatches.length > 0) {
    return upcomingPleMatches[0]
  }

  return Array.isArray(insight?.announcedShows)
    ? insight.announcedShows.find(show => show?.isPleAnnouncement) || null
    : null
}

const sortByRecentPerformance = (a, b) =>
  b.recentPoints - a.recentPoints ||
  b.totalScore - a.totalScore ||
  getMemberName(a).localeCompare(getMemberName(b), 'it')

export const buildLineupRoleAdvice = ({
  isPremium = false,
  premiumInsightsLoading = false,
  members = [],
  premiumInsights = {},
  hasMainEventer = true,
  hasBackstage = false,
  maxBackstage = 0,
  maxRing = 0,
  resolveMainEventerCooldownUntil = () => null
} = {}) => {
  const emptyAdvice = {
    hasAny: false,
    byId: {},
    mainEventer: null,
    mainEventerPleCandidates: [],
    inRing: [],
    backstage: []
  }

  if (!isPremium || premiumInsightsLoading || members.length === 0) {
    return emptyAdvice
  }

  const enrichedMembers = members.map(member => {
    const insight = premiumInsights[member.id] || {}
    return {
      member,
      recentPoints: toLineupAdviceNumber(
        insight.lastThreeShowPoints ?? insight.recentPoints
      ),
      totalScore: toLineupAdviceNumber(member.score ?? member.currentScore),
      upcomingPleMatch: getUpcomingPleMatch(insight),
      isInactive: member.active === false,
      isInjured: Boolean(member.isInjured),
      cooldownUntil: hasMainEventer
        ? resolveMainEventerCooldownUntil(member.id)
        : null
    }
  })

  const byRecentPerformance = [...enrichedMembers].sort(sortByRecentPerformance)

  const eligiblePleCandidates = enrichedMembers.filter(
    item =>
      item.upcomingPleMatch &&
      !item.isInactive &&
      !item.isInjured &&
      !item.cooldownUntil
  )
  const closestPleTime = eligiblePleCandidates.reduce(
    (closestTime, item) => Math.min(closestTime, getUpcomingPleTime(item)),
    Number.MAX_SAFE_INTEGER
  )
  const isClosestPleCandidate = item =>
    Boolean(item?.upcomingPleMatch) && getUpcomingPleTime(item) === closestPleTime

  const byMainEventerPriority = [...enrichedMembers].sort(
    (a, b) =>
      Number(isClosestPleCandidate(b)) - Number(isClosestPleCandidate(a)) ||
      Number(Boolean(b.upcomingPleMatch)) - Number(Boolean(a.upcomingPleMatch)) ||
      getUpcomingPleTime(a) - getUpcomingPleTime(b) ||
      sortByRecentPerformance(a, b)
  )

  const mainEventer = hasMainEventer
    ? byMainEventerPriority.find(
        item => !item.isInactive && !item.isInjured && !item.cooldownUntil
      ) ||
      null
    : null

  const mainEventerPleCandidates = hasMainEventer
    ? byMainEventerPriority.filter(
        item =>
          item.upcomingPleMatch &&
          !item.isInactive &&
          !item.isInjured &&
          !item.cooldownUntil
      )
    : []

  const backstage =
    hasBackstage && maxBackstage > 0
      ? [...enrichedMembers]
          .filter(item => item.member.id !== mainEventer?.member?.id)
          .sort(
            (a, b) =>
              Number(b.isInactive) - Number(a.isInactive) ||
              Number(b.isInjured) - Number(a.isInjured) ||
              a.recentPoints - b.recentPoints ||
              a.totalScore - b.totalScore ||
              getMemberName(a).localeCompare(getMemberName(b), 'it')
          )
          .slice(0, maxBackstage)
      : []

  const backstageIds = new Set(backstage.map(item => item.member.id))
  const availableInRingSlots =
    hasMainEventer && mainEventer ? Math.max(0, maxRing - 1) : maxRing
  const inRing = byRecentPerformance
    .filter(
      item =>
        !backstageIds.has(item.member.id) &&
        item.member.id !== mainEventer?.member?.id
    )
    .slice(0, availableInRingSlots)

  const byId = {}
  const addAdvice = (item, role, primary) => {
    if (!item?.member?.id) return

    const existing = byId[item.member.id] || {
      primary,
      roles: [],
      recentPoints: item.recentPoints,
      upcomingPleMatch: item.upcomingPleMatch || null,
      isInactive: item.isInactive,
      isInjured: item.isInjured
    }

    existing.roles = [...new Set([...existing.roles, role])]
    existing.recentPoints = item.recentPoints
    existing.upcomingPleMatch =
      item.upcomingPleMatch || existing.upcomingPleMatch || null
    existing.isInactive = item.isInactive
    existing.isInjured = item.isInjured

    if (primary === 'main-eventer' || !existing.primary) {
      existing.primary = primary
    }

    byId[item.member.id] = existing
  }

  inRing.forEach(item => addAdvice(item, 'In-Ring', 'in-ring'))
  backstage.forEach(item => addAdvice(item, 'Backstage', 'backstage'))
  mainEventerPleCandidates.forEach(item =>
    addAdvice(item, 'Main Eventer', 'main-eventer')
  )
  addAdvice(mainEventer, 'Main Eventer', 'main-eventer')

  return {
    hasAny: Boolean(mainEventer || inRing.length || backstage.length),
    byId,
    mainEventer,
    mainEventerPleCandidates,
    inRing,
    backstage
  }
}
